/**
 * Campaign API Service
 *
 * File: Mythbound.App/src/services/api/CampaignService.ts
 * Created: 2026-01-06
 * Last Modified: 2026-01-06
 *
 * PURPOSE:
 * HTTP client for campaign CRUD operations, participant management,
 * invitation token generation, and session lifecycle.
 *
 * USAGE:
 * const campaignService = new CampaignService();
 * const campaigns = await campaignService.listCampaigns();
 * const newCampaign = await campaignService.createCampaign(request);
 *
 * ERROR HANDLING:
 * - 400 Bad Request: Invalid campaign data (return error message)
 * - 403 Forbidden: User not authorized (tier limit or not GM)
 * - 404 Not Found: Campaign doesn't exist
 * - 500 Server Error: Server failure (retry logic)
 *
 * FRONTEND INTEGRATION:
 * - Called by Dashboard, CampaignBuilder, CampaignPage
 * - Results stored in CampaignContext
 * - Errors shown via toast notifications
 *
 * CACHING STRATEGY:
 * - No caching in MVP (always fetch fresh data)
 * - Cache-bust on create/update/delete
 * - [FUTURE] SWR or React Query for smart caching
 *
 * Pipeline: Frontend components → CampaignService → Backend /api/campaigns → Database
 * Dependencies: fetch API, type definitions from shared contracts
 *
 * Updated 2026-09-21 (PRD001A 001A.7a): added structured `ApiError` handling
 * (status + `kind`) in place of a generic `Error`, per the 001A.1 inventory
 * finding that a non-JSON error body (e.g. a plain-text 503) previously threw
 * an unrelated JSON-parse error instead of a usable status/message, and that
 * a thrown network error was not normalized at all.
 *
 * Corrected 2026-09-21 (same wave, parent review round 2): every call now
 * goes through `authorizedFetch` (src/services/api/authTransport.ts) instead
 * of a locally built `Authorization` header. This sends `credentials:
 * 'include'` PLUS a bearer header whenever a non-expired access token is on
 * hand — the earlier version of this comment removed the bearer entirely on
 * the assumption that browser auth was going cookie-only; that assumption
 * was wrong. mythbound.app (frontend) and the API (onrender.com) are
 * cross-site, and Safari/other third-party-cookie-blocking browsers never
 * send the session cookie there, so bearer must stay as an always-available
 * fallback transport sent alongside the cookie. `authorizedFetch` also
 * implements the backend's single refresh-and-retry contract for a
 * `401 { code: 'token_expired' }` response. This remains a transport choice
 * only — `AuthContext.checkSessionStatus`'s `GET /api/auth/session` response
 * is still the only source of truth for identity.
 */

import { getApiBaseUrl } from '../../utils/environment';
import { ApiError, apiErrorFromResponse, apiErrorFromNetworkFailure } from './ApiError';
import { authorizedFetch } from './authTransport';

export interface Campaign {
  campaign_id: string;
  campaign_name: string;
  description: string;
  creator_user_id: string;
  created_at: string;
  game_system: string;
  max_participants: number;
  participant_count: number;
  campaign_settings: Record<string, any>;
  session_zero_config: Record<string, any>;
  is_active: boolean;
}

export interface CampaignListItem {
  campaign_id: string;
  campaign_name: string;
  description: string;
  participant_count: number;
  max_participants: number;
  next_session: string | null;
  last_activity: string;
  game_system: string;
}

export interface CampaignCreateRequest {
  campaign_name: string;
  description: string;
  game_system: string;
  max_participants?: number;
  campaign_settings?: Record<string, any>;
  session_zero_config?: Record<string, any>;
}

export interface CampaignListResponse {
  campaigns: CampaignListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface InvitationResponse {
  token: string;
  campaign_id: string;
  created_at: string;
  expires_at: string;
  shareable_url: string;
  /**
   * PRD001A 001A.6: needed to call `revokeInvitation`. Always populated by
   * the real `create_invitation` response as of 001A.6 (the backend's own
   * Pydantic field stays `Optional` for response-shape back-compat only —
   * every actual response includes it).
   */
  invitation_id: string;
}

/**
 * GET /api/invite/{token} — anonymous preview (PRD001A 001A.6). This is the
 * COMPLETE allowlist the backend ever returns for a valid token; never
 * account/provider IDs, email, private sheets, or session content (see
 * `shared/contracts/campaign/v1/campaign-management.yaml`'s
 * `WebInvitationAdmission_001A6.preview` section).
 */
export interface InvitePreviewResponse {
  campaign_id: string;
  campaign_name: string;
  expires_at: string | null;
  /** Display screen names only — never user_id/account IDs. */
  participant_screen_names: string[];
  /** Visibility-respecting; archived characters excluded. */
  character_names: string[];
  prior_session_count: number;
}

/**
 * Machine-readable error codes the 001A.6 invite/join contract defines, all
 * carried in the response body as `{"detail": {"message": "...", "code":
 * "..."}}`. Several share an HTTP status (e.g. `invite_expired`/
 * `invite_revoked`/`campaign_full` are all 409) — `ApiError.kind` alone
 * cannot distinguish them; use `getInviteErrorCode`/`getInviteErrorMessage`.
 */
export type InviteErrorCode =
  | 'invite_invalid'
  | 'invite_expired'
  | 'invite_revoked'
  | 'campaign_full'
  | 'not_found'
  | 'forbidden';

const KNOWN_INVITE_ERROR_CODES: InviteErrorCode[] = [
  'invite_invalid',
  'invite_expired',
  'invite_revoked',
  'campaign_full',
  'not_found',
  'forbidden',
];

/**
 * Extract the invite contract's `detail.code` from a thrown `ApiError`, if
 * present. Returns `null` for a non-`ApiError`, a body with no nested
 * `detail.code`, or a code this contract doesn't define (never throws).
 */
export function getInviteErrorCode(error: unknown): InviteErrorCode | null {
  if (!(error instanceof ApiError)) return null;
  const body = error.detail;
  if (!body || typeof body !== 'object') return null;
  const nested = (body as Record<string, unknown>).detail;
  if (!nested || typeof nested !== 'object') return null;
  const code = (nested as Record<string, unknown>).code;
  return typeof code === 'string' && (KNOWN_INVITE_ERROR_CODES as string[]).includes(code)
    ? (code as InviteErrorCode)
    : null;
}

/**
 * Honest, per-code messages for the invite/join contract (never the
 * backend's own deliberately generic `detail.message` — see the contract's
 * `error_contract.shape` note). `campaign_full` reads the `current_count`/
 * `max_participants` extra fields `_error()` (campaign_service.py) attaches,
 * when present, for a more useful message; falls back to a generic one.
 */
export function getInviteErrorMessage(error: unknown, fallbackMessage: string): string {
  const code = getInviteErrorCode(error);
  if (code === 'invite_invalid') return 'This invite link is not valid.';
  if (code === 'invite_expired') return 'This invite link has expired.';
  if (code === 'invite_revoked') return 'This invite link has been revoked by the GM.';
  if (code === 'not_found') return 'This campaign no longer exists.';
  if (code === 'forbidden') return "You don't have permission to do that.";
  if (code === 'campaign_full') {
    const body = error instanceof ApiError && error.detail && typeof error.detail === 'object'
      ? ((error.detail as Record<string, unknown>).detail as Record<string, unknown> | undefined)
      : undefined;
    const current = body?.current_count;
    const max = body?.max_participants;
    return typeof current === 'number' && typeof max === 'number'
      ? `This campaign is full (${current}/${max}).`
      : 'This campaign is full.';
  }
  if (error instanceof ApiError) return error.message;
  return fallbackMessage;
}

/**
 * Campaign participant with optional character data.
 *
 * WHY here: CampaignContext previously defined this locally, but now that
 * CampaignService owns the fetch (getParticipants), the type belongs here
 * alongside the other campaign-domain interfaces.
 *
 * Contract: shared/contracts/campaign/v1/campaign-management.yaml § participants_endpoint
 * Added: 2026-04-14
 * See: PRD_CampaignParticipants_v1.md § Phase 3
 */
export interface Participant {
  user_id: string;
  screen_name: string;
  role: 'gm' | 'player' | 'spectator';
  joined_at: string;
  character_id?: string | null;
  character_name?: string | null;
}

/** POST /api/campaigns/{id}/participants response (backend `ParticipantResponse`). */
export interface JoinCampaignResponse {
  id: string;
  campaign_id: string;
  user_id: string;
  screen_name: string;
  role: string;
  joined_at: string;
}

/** Every invite-related method accepts this optional bag; currently only `signal` is used. */
export interface CampaignRequestOptions {
  signal?: AbortSignal;
}

  export class CampaignService {
  private apiBaseUrl = getApiBaseUrl();

  /**
   * Create new campaign with tier-based participant limits
   *
   * PARAMETERS:
   * @param request CampaignCreateRequest with name, description, game_system
   *
   * RETURNS:
   * @returns Campaign object with campaign_id
   *
   * THROWS:
   * @throws Error with message "Failed to create campaign"
   *
   * EXAMPLE USAGE:
   * const newCampaign = await campaignService.createCampaign({
   *   campaign_name: "Dragon's Lair",
   *   description: "A perilous journey...",
   *   game_system: "dnd5e"
   * });
   *
   * TIER ENFORCEMENT:
   * - Free tier: Max 4 participants (soft limit with warning)
   * - Premium tier: Max 6 participants (soft limit with warning)
   * - Admin: Unlimited
   *
   * Created: 2026-01-06
   */
  async createCampaign(request: CampaignCreateRequest): Promise<Campaign> {
    let response: Response;
    try {
      response = await authorizedFetch(`${this.apiBaseUrl}/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request)
      });
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to create campaign');
    }

    if (!response.ok) {
      throw await apiErrorFromResponse(response, 'Failed to create campaign');
    }

    return response.json();
  }

  /**
   * List campaigns for authenticated user (paginated)
   *
   * PARAMETERS:
   * @param includeArchived Include archived campaigns (default: false)
   * @param limit Max campaigns to return (default: 20, max: 100)
   * @param offset Pagination offset (default: 0)
   *
   * RETURNS:
   * @returns CampaignListResponse { campaigns, total, limit, offset }
   *
   * CACHING:
   * - No cache in MVP
   * - [FUTURE] Cache with 5-minute TTL
   *
   * USAGE:
   * const result = await campaignService.listCampaigns();
   * console.log(`User has ${result.total} campaigns`);
   *
   * Created: 2026-01-06
   */
  async listCampaigns(
    includeArchived = false,
    limit = 20,
    offset = 0
  ): Promise<CampaignListResponse> {
    const params = new URLSearchParams({
      include_archived: String(includeArchived),
      limit: String(limit),
      offset: String(offset)
    });

    let response: Response;
    try {
      response = await authorizedFetch(`${this.apiBaseUrl}/campaigns?${params}`, {
        method: 'GET',
      });
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to load campaigns');
    }

    // Preserves the exact 'UNAUTHORIZED' message existing callers match on
    // (e.g. DashboardCampaignSection.tsx `error === 'UNAUTHORIZED'`), while
    // also carrying the structured `kind: 'unauthenticated'` for new callers.
    if (response.status === 401) {
      throw new ApiError('UNAUTHORIZED', 401, 'unauthenticated');
    }
    if (!response.ok) {
      throw await apiErrorFromResponse(response, 'Failed to load campaigns');
    }

    return response.json();
  }

  /**
   * Get single campaign by ID with full details
   *
   * PARAMETERS:
   * @param campaignId Campaign UUID
   *
   * RETURNS:
   * @returns Campaign object with full details (participants, settings, Session Zero config)
   *
   * ERROR CODES:
   * - 404: Campaign not found
   * - 403: User not authorized to view this campaign
   *
   * Created: 2026-01-06
   */
  async getCampaign(campaignId: string): Promise<Campaign> {
    let response: Response;
    try {
      response = await authorizedFetch(`${this.apiBaseUrl}/campaigns/${campaignId}`, {
        method: 'GET',
      });
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to load campaign');
    }

    if (!response.ok) {
      throw await apiErrorFromResponse(
        response,
        response.status === 404 ? 'Campaign not found' : 'Failed to load campaign'
      );
    }

    return response.json();
  }

  /**
   * REMOVED 2026-09-22 (PRD001A 001A.7b, caller-contract cross-check):
   * `updateCampaign(campaignId, updates)` targeted `PUT /api/campaigns/{id}`,
   * a route that does not exist in the backend's configured route manifest
   * (`src/__tests__/fixtures/configured_routes_001a.json` — confirmed via
   * `run_backend_checks.py`'s `app.state.build_runtime_manifest()`; no
   * `PUT /api/campaigns/{campaign_id}` route is registered). Grepped the
   * whole `src/` tree for real callers before removing: zero — the only
   * other `updateCampaign` in this codebase is `CampaignContext.tsx`'s own
   * local `useCallback` (a same-named but unrelated in-memory state
   * setter, called from `useCampaignWebSocket.ts` on a
   * `campaign.updated`-style broadcast), never this HTTP method. This was
   * always a dead, unreachable network call — never wired to any campaign
   * settings UI. If campaign-settings editing is built later, it needs a
   * real backend endpoint first; this method is not a template to restore
   * as-is.
   */

  /**
   * Create invitation token for campaign
   *
   * PARAMETERS:
   * @param campaignId Campaign UUID
   * @param expiresInDays Token expiry in days (default: 7)
   *
   * RETURNS:
   * @returns InvitationResponse { token, shareable_url, expires_at }
   *
   * AUTHORIZATION:
   * - Only GM (campaign creator) can create tokens
   *
   * USAGE:
   * const invite = await campaignService.createInvitation('camp_123');
   * // Show user the invite.shareable_url to copy and share
   *
   * [FUTURE CODESPACE] Screen name search alternative to tokens
   *
   * Created: 2026-01-06
   */
  async createInvitation(campaignId: string, expiresInDays = 7): Promise<InvitationResponse> {
    let response: Response;
    try {
      response = await authorizedFetch(`${this.apiBaseUrl}/campaigns/${campaignId}/invitations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // 001A.6: expiresInDays is accepted for request-shape back-compat
        // only — the backend now IGNORES it and always uses a fixed 7-day
        // lifetime (campaign_service.INVITATION_LIFETIME_DAYS). Kept as a
        // parameter here rather than removed, so existing call sites don't
        // need a signature change for a value the server no longer reads.
        body: JSON.stringify({ expires_in_days: expiresInDays })
      });
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to create invitation');
    }

    if (!response.ok) {
      throw await apiErrorFromResponse(response, 'Failed to create invitation');
    }

    return response.json();
  }

  /**
   * Revoke an invitation token (PRD001A 001A.6, GM-only).
   *
   * Sets the invitation's `revoked` flag — NEVER removes any membership
   * already granted through it (revoking a token never un-joins anyone who
   * already used it).
   *
   * @throws {ApiError} 404 not_found, 403 forbidden (not GM), or a
   *   network/unavailable failure.
   */
  async revokeInvitation(
    campaignId: string,
    invitationId: string,
    options?: CampaignRequestOptions
  ): Promise<void> {
    let response: Response;
    try {
      response = await authorizedFetch(
        `${this.apiBaseUrl}/campaigns/${campaignId}/invitations/${invitationId}/revoke`,
        { method: 'POST', signal: options?.signal }
      );
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to revoke invitation');
    }
    if (!response.ok) {
      throw await apiErrorFromResponse(response, 'Failed to revoke invitation');
    }
  }

  /**
   * Anonymous invite preview — GET /api/invite/{token} (PRD001A 001A.6).
   *
   * Works with NO login (`authorizedFetch` sends a cookie/bearer only when
   * one already exists; it degrades to a plain anonymous request otherwise
   * — this endpoint never requires or checks authentication). Read-only:
   * never increments the token's use count. Returns the COMPLETE allowlist
   * — see `InvitePreviewResponse`.
   *
   * @throws {ApiError} `getInviteErrorCode(err)` yields `invite_invalid` /
   *   `invite_expired` / `invite_revoked` (never any campaign detail in the
   *   body for those), or a network/unavailable failure.
   */
  async getInvitePreview(token: string, options?: CampaignRequestOptions): Promise<InvitePreviewResponse> {
    // encodeURIComponent preserves exact dot segments, which URL parsers
    // normalize as parent/current paths. They cannot be invitation tokens.
    if (token === '.' || token === '..') {
      throw new ApiError('This invite link is not valid.', 404, 'not_found', {
        detail: { code: 'invite_invalid' },
      });
    }
    let response: Response;
    try {
      response = await authorizedFetch(`${this.apiBaseUrl}/invite/${encodeURIComponent(token)}`, {
        method: 'GET',
        signal: options?.signal,
      });
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to load invite');
    }
    if (!response.ok) {
      throw await apiErrorFromResponse(response, 'Failed to load invite');
    }
    // Treat an incomplete/malformed success as unavailable, never as an empty
    // roster or an invalid token. Pick the public DTO explicitly so callers
    // cannot accidentally retain a future private field in preview state.
    try {
      const body = await response.json();
      if (
        !body || typeof body.campaign_id !== 'string' || !body.campaign_id ||
        typeof body.campaign_name !== 'string' ||
        !(body.expires_at === null || (typeof body.expires_at === 'string' && Number.isFinite(Date.parse(body.expires_at)))) ||
        !Array.isArray(body.participant_screen_names) || !body.participant_screen_names.every((name: unknown) => typeof name === 'string') ||
        !Array.isArray(body.character_names) || !body.character_names.every((name: unknown) => typeof name === 'string') ||
        !Number.isInteger(body.prior_session_count) || body.prior_session_count < 0
      ) {
        throw new Error('Incomplete invitation preview');
      }
      return {
        campaign_id: body.campaign_id,
        campaign_name: body.campaign_name,
        expires_at: body.expires_at,
        participant_screen_names: body.participant_screen_names,
        character_names: body.character_names,
        prior_session_count: body.prior_session_count,
      };
    } catch {
      throw new ApiError('We could not read the invitation preview. Please try again.', response.status, 'unavailable');
    }
  }

  /**
   * Join campaign with invitation token.
   *
   * `POST /api/campaigns/{id}/participants` — the ONLY join operation (there
   * is no `/join` route; PRD001A 001A.6 consolidated onto this one path).
   *
   * TIER ENFORCEMENT (HARD, 001A.6 — corrects a previously-documented "soft
   * limit with warning" that no longer matches the backend): capacity is
   * resolved from the campaign CREATOR's live tier (free=4, premium=6,
   * admin=100), counting the creator. At capacity, this throws with
   * `getInviteErrorCode(err) === 'campaign_full'` — there is no
   * warning-only overflow and no `campaign.tier_limit_warning` event.
   *
   * IDEMPOTENT: joining with an already-held membership returns 200 with
   * the existing membership row — no error, no duplicate slot consumed. The
   * response body has no field distinguishing this from a brand-new join;
   * callers that need to show a distinct "already a member" state (see
   * `InviteLanding.tsx`) check membership BEFORE calling this, via
   * `getCampaign()`.
   *
   * WEBSOCKET: a genuinely NEW join broadcasts `campaign.participant_added`
   * — roster updates automatically via polling (2-5s).
   *
   * @throws {ApiError} `getInviteErrorCode(err)` yields `invite_invalid` /
   *   `invite_expired` / `invite_revoked` / `campaign_full`, or a
   *   network/unavailable failure.
   */
  async joinCampaignWithToken(
    campaignId: string,
    invitationToken: string,
    options?: CampaignRequestOptions
  ): Promise<JoinCampaignResponse> {
    let response: Response;
    try {
      response = await authorizedFetch(`${this.apiBaseUrl}/campaigns/${campaignId}/participants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invitation_token: invitationToken }),
        signal: options?.signal,
      });
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to join campaign');
    }

    if (!response.ok) {
      throw await apiErrorFromResponse(
        response,
        response.status === 404 ? 'Invalid invitation or campaign not found' : 'Failed to join campaign'
      );
    }

    // A 2xx status alone cannot acknowledge membership. Require the existing
    // participant DTO bound to the requested campaign before the landing page
    // may claim joined or redirect; retries use the server's idempotent join.
    try {
      const body = await response.json();
      if (
        !body || typeof body.id !== 'string' || !body.id ||
        body.campaign_id !== campaignId ||
        typeof body.user_id !== 'string' || !body.user_id ||
        typeof body.screen_name !== 'string' ||
        typeof body.role !== 'string' || !body.role ||
        typeof body.joined_at !== 'string' || !Number.isFinite(Date.parse(body.joined_at))
      ) {
        throw new Error('Incomplete membership acknowledgment');
      }
      return {
        id: body.id, campaign_id: body.campaign_id, user_id: body.user_id,
        screen_name: body.screen_name, role: body.role, joined_at: body.joined_at,
      };
    } catch {
      throw new ApiError('We could not confirm campaign membership. Please try again.', response.status, 'unavailable');
    }
  }

  /**
   * REMOVED 2026-09-21 (PRD001A 001A.4 Wave 3): `startSession`/`endSession`
   * used to be duplicated here and in `SessionService.ts` with the same
   * URLs (flagged in the 001A.1 inventory as a consistency risk). Checked
   * before removing: `grep`-ing the whole `src/` tree found the only actual
   * callers were `SessionContext.startSession`/`endSession`, which already
   * call `sessionService.startSession`/`endSession` — `CampaignPage.tsx`'s
   * "Play Myth" button calls `useSession().startSession`, i.e. the
   * `SessionService` copy, not this one. This class's copies had zero
   * callers anywhere and are removed rather than kept as a second,
   * divergent implementation. `SessionStartRequest`/`SessionResponse` were
   * removed alongside them (unused outside this file).
   */

  /**
   * Get the full participant list for a campaign.
   *
   * WHY: GET /api/campaigns/{id} only returns participant_count (integer).
   * This dedicated endpoint returns the full list with screen names, roles,
   * and optional character data (character_id, character_name via LEFT JOIN).
   *
   * Access: Campaign members only (backend enforces 403 for non-members).
   * Contract: shared/contracts/campaign/v1/campaign-management.yaml § participants_endpoint
   * Added: 2026-04-14
   * See: PRD_CampaignParticipants_v1.md § Phase 3
   */
  async getParticipants(campaignId: string): Promise<Participant[]> {
    let response: Response;
    try {
      response = await authorizedFetch(
        `${this.apiBaseUrl}/campaigns/${campaignId}/participants`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
    } catch (error) {
      throw apiErrorFromNetworkFailure(error, 'Failed to load participants');
    }
    if (!response.ok) {
      throw await apiErrorFromResponse(response, `Failed to load participants (${response.status})`);
    }
    return response.json();
  }
}

// Export singleton instance
export const campaignService = new CampaignService();

